import {
  useEffect,
  useRef,
  useCallback,
  createContext,
  useContext,
  useState,
} from 'react';
import { useRouter } from 'next/router';

type NavContextType = {
  history: string[];
  push: (pathname: string, query?: Query) => void;
  back: () => void;
  queryParameters: Record<string, string>;
};

type Query = { [key: string]: string | number };

const NavContext = createContext<NavContextType>({
  back: () => {},
  history: [],
  push: () => {},
  queryParameters: {},
});

export const NavProvider = ({ children }: any) => {
  const router = useRouter();
  const historyReference = useRef<string[]>([]);
  const [queryParameters, setQueryParameters] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    // This event seems to fire off more than once for a single event. Only update the historyRef.current if its a new url.
    const handleRouteChange = (url: string) => {
      const [pathname, search] = url.split('?');

      if (
        historyReference.current.length === 0 ||
        historyReference.current[historyReference.current.length - 1] !==
          pathname
      ) {
        historyReference.current = [...historyReference.current, pathname];
      }

      if (search) {
        const parameters = new URLSearchParams(search);
        setQueryParameters((oldParameters) => ({
          ...oldParameters,
          ...Object.fromEntries(parameters.entries()),
        }));
      }
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [router]);

  const back = useCallback(() => {
    if (historyReference.current.length <= 1) {
      router.push('/card');
      return;
    }

    const newHistory = historyReference.current.slice(0, -1);
    const previousUrl = newHistory[newHistory.length - 1] || '/card';

    router.push(previousUrl, undefined, { shallow: true }).then(() => {
      historyReference.current = newHistory;
    });
  }, [router]);

  return (
    <NavContext.Provider
      value={{
        back,
        history: historyReference.current,
        push: router.push,
        queryParameters,
      }}
    >
      {children}
    </NavContext.Provider>
  );
};

// TODO change to useNavWithHistory?
export const useNavWithTokens = () => useContext(NavContext);
